import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { membershipAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Zap, ArrowLeft, Crown, Check } from 'lucide-react';

const Membership = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await membershipAPI.getPlans();
      setPlans(response.data);
    } catch (error) {
      toast.error('Failed to fetch plans');
    }
  };

  const handleSubscribe = async () => {
    if (!localStorage.getItem('token')) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const response = await membershipAPI.createCheckout({
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              LuxLyx
            </span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/products')}
          className="mb-8 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="text-center mb-16">
          <Crown className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
          <h1 className="text-5xl font-bold mb-4">Unlock Premium Benefits</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Get exclusive access to early sales, extra discounts, and AI-powered recommendations
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              data-testid={`plan-${plan.id}`}
              className={`bg-gradient-to-br rounded-3xl p-8 border-2 ${
                plan.id === 'premium'
                  ? 'from-yellow-400/20 to-orange-500/20 border-yellow-400/50 scale-105'
                  : 'from-[#1a1a1c] to-[#0f0f10] border-white/10'
              }`}
            >
              {plan.id === 'premium' && (
                <div className="inline-flex items-center space-x-2 bg-yellow-400 text-black rounded-full px-4 py-1 mb-4 font-bold text-sm">
                  <Crown className="h-4 w-4" />
                  <span>RECOMMENDED</span>
                </div>
              )}
              
              <h3 className="text-3xl font-bold mb-2">{plan.name}</h3>
              <div className="mb-6">
                {plan.price === 0 ? (
                  <div className="text-4xl font-bold">Free</div>
                ) : (
                  <div>
                    <span className="text-5xl font-bold text-yellow-400">${plan.price}</span>
                    <span className="text-gray-400">/month</span>
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-8">
                {plan.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Check className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>

              {plan.id === 'premium' ? (
                user.membership_type === 'premium' ? (
                  <Button
                    disabled
                    className="w-full bg-gray-600 text-white font-bold py-6 text-lg cursor-not-allowed"
                  >
                    Current Plan
                  </Button>
                ) : (
                  <Button
                    data-testid="subscribe-btn"
                    onClick={handleSubscribe}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold py-6 text-lg hover:opacity-90"
                  >
                    {loading ? 'Processing...' : 'Upgrade to Premium'}
                  </Button>
                )
              ) : (
                <Button
                  disabled
                  className="w-full bg-white/5 text-gray-400 font-bold py-6 text-lg cursor-not-allowed"
                >
                  Current Plan
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Membership;
